import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Divider,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  LinearProgress,
  Autocomplete
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Preview as PreviewIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Description as TemplateIcon,
  Save as SaveIcon,
  FileDownload as ExportIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  EmailTemplate,
  EmailComposerData,
  Candidate,
  ProcessedEmail,
  User as UserType
} from '../types';
import emailService from '../services/emailService';
import airtableService from '../services/airtable';
import { EmailExportDialog, useEmailExport } from './EmailExport';

interface EmailComposerProps {
  open: boolean;
  onClose: () => void;
  recipients: Candidate[];
  defaultTemplate?: EmailTemplate;
  onSend: (emailData: EmailComposerData) => Promise<void>;
  onSave?: (draft: EmailComposerData) => Promise<void>;
}

const EmailComposer: React.FC<EmailComposerProps> = ({
  open,
  onClose,
  recipients,
  defaultTemplate,
  onSend,
  onSave
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(defaultTemplate || null);
  const [selectedRecipients, setSelectedRecipients] = useState<Candidate[]>([]);
  const [ccUsers, setCcUsers] = useState<UserType[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [priority, setPriority] = useState<'High' | 'Normal' | 'Low'>('Normal');
  const [scheduledSend, setScheduledSend] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<Date | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<ProcessedEmail | null>(null);
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Email Export functionality
  const { exportState, openExportDialog, closeExportDialog } = useEmailExport();

  // Load email templates and users on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await emailService.getEmailTemplates();
        if (response.success) {
          setTemplates(response.data);
        }
      } catch (err) {
        console.error('Error loading email templates:', err);
      }
    };

    const loadUsers = async () => {
      try {
        const response = await airtableService.getUsers();
        if (response.success) {
          setUsers(response.data);
        }
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };

    if (open) {
      loadTemplates();
      loadUsers();
    }
  }, [open]);

  // Set default template data when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setSubject(selectedTemplate.subjectLine);
      setEmailBody(selectedTemplate.emailBody);
    }
  }, [selectedTemplate]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(null);
      setPreviewMode(false);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate);
      }
    } else {
      // Reset form when closing
      setSubject('');
      setEmailBody('');
      setCustomMessage('');
      setScheduledSend(false);
      setScheduledTime(null);
      setPreviewMode(false);
      setPreviewEmail(null);
    }
  }, [open, defaultTemplate]);

  const handleTemplateChange = (template: EmailTemplate | null) => {
    setSelectedTemplate(template);
    if (template) {
      setSubject(template.subjectLine);
      setEmailBody(template.emailBody);
    }
  };

  const insertVariable = (variable: string) => {
    const placeholder = `{${variable}}`;
    setEmailBody(prev => prev + ' ' + placeholder);
  };

  const generatePreview = async () => {
    if (selectedRecipients.length === 0) {
      setError('No recipients selected');
      return;
    }

    try {
      setLoading(true);
      const firstRecipient = selectedRecipients[0];

      // Extract variables from first recipient
      const variables = await emailService.extractVariables(firstRecipient);

      // Add custom message if provided
      if (customMessage) {
        variables.customMessage = customMessage;
      }

      // Create a mock template for preview
      const mockTemplate: EmailTemplate = {
        id: 'preview',
        templateName: 'Preview',
        subjectLine: subject,
        emailBody: emailBody,
        templateCategory: 'General',
        variablesUsed: [],
        active: true,
        createdBy: 'user',
        createdDate: new Date(),
        lastModified: new Date()
      };

      const processed = await emailService.processTemplate(mockTemplate, variables);
      setPreviewEmail(processed);
      setPreviewMode(true);
    } catch (err) {
      setError('Failed to generate preview');
      console.error('Preview error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }

    if (!emailBody.trim()) {
      setError('Email body is required');
      return;
    }

    if (selectedRecipients.length === 0) {
      setError('At least one recipient is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ccEmails = ccUsers.length > 0 ? ccUsers.map(u => u.email) : undefined;

      const emailData: EmailComposerData = {
        recipients: selectedRecipients.map(r => r.id),
        cc: ccEmails,
        templateId: selectedTemplate?.id,
        subject,
        body: emailBody,
        scheduledSendTime: scheduledSend ? scheduledTime || undefined : undefined,
        priority,
        variables: {
          customMessage: customMessage || undefined
        }
      };

      await onSend(emailData);
      const ccMessage = ccUsers.length > 0 ? ` (CC: ${ccUsers.length} user(s))` : '';
      setSuccess(`Email ${scheduledSend ? 'scheduled' : 'sent'} to ${selectedRecipients.length} recipient(s)${ccMessage}`);

      // Close dialog after successful send
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!onSave) return;

    try {
      setLoading(true);

      const emailData: EmailComposerData = {
        recipients: selectedRecipients.map(r => r.id),
        cc: ccUsers.length > 0 ? ccUsers.map(u => u.email) : undefined,
        templateId: selectedTemplate?.id,
        subject,
        body: emailBody,
        priority,
        variables: {
          customMessage: customMessage || undefined
        }
      };

      await onSave(emailData);
      setSuccess('Draft saved successfully');

    } catch (err) {
      setError('Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const availableVariables = [
    'firstName', 'lastName', 'email', 'phone',
    'positionTitle', 'companyName', 'department',
    'applicationStatus', 'applicationDate',
    'interviewDate', 'interviewTime', 'interviewLocation',
    'meetingLink', 'interviewerName',
    'currentStep', 'nextSteps', 'customMessage'
  ];

  if (previewMode && previewEmail) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Email Preview</Typography>
            <IconButton onClick={() => setPreviewMode(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                To: {previewEmail.to}
              </Typography>
              <Typography variant="h6" gutterBottom>
                {previewEmail.subject}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box
                component="div"
                sx={{
                  lineHeight: 1.6,
                  '& p': { margin: '0.5em 0' },
                  '& br': { lineHeight: 1.6 },
                  '& strong': { fontWeight: 'bold' },
                  '& em': { fontStyle: 'italic' },
                  '& ul, & ol': { paddingLeft: '20px', margin: '0.5em 0' },
                  '& li': { marginBottom: '0.25em' },
                  '& a': { color: '#1976d2', textDecoration: 'underline' },
                  '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '0.5em 0', fontWeight: 'bold' }
                }}
                dangerouslySetInnerHTML={{
                  __html: previewEmail.body
                }}
              />
            </CardContent>
          </Card>
          <Alert severity="info" sx={{ mt: 2 }}>
            This preview shows how the email will appear to {selectedRecipients[0]?.firstName} {selectedRecipients[0]?.lastName}.
            Other recipients will see personalized versions.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewMode(false)}>
            Back to Edit
          </Button>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={loading}
            startIcon={scheduledSend ? <ScheduleIcon /> : <SendIcon />}
          >
            {scheduledSend ? 'Schedule Email' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <EmailIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Compose Email</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gap: 3 }}>
            {/* Recipients Selector */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Recipients
              </Typography>
              <Autocomplete
                multiple
                options={recipients}
                value={selectedRecipients}
                onChange={(event, newValue) => {
                  setSelectedRecipients(newValue);
                }}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select candidates..."
                    helperText={`${selectedRecipients.length} recipient(s) selected`}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      icon={<PersonIcon />}
                      label={`${option.firstName} ${option.lastName}`}
                      size="small"
                    />
                  ))
                }
              />
            </Box>

            {/* CC Users Selector */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                CC (Copy to Users)
              </Typography>
              <Autocomplete
                multiple
                options={users}
                value={ccUsers}
                onChange={(event, newValue) => {
                  setCcUsers(newValue);
                }}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select users to CC..."
                    helperText={`${ccUsers.length} user(s) will receive a copy`}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      icon={<EmailIcon />}
                      label={`${option.firstName} ${option.lastName}`}
                      size="small"
                    />
                  ))
                }
              />
            </Box>

            {/* Template Selection */}
            <FormControl fullWidth>
              <InputLabel>Email Template</InputLabel>
              <Select
                value={selectedTemplate?.id || ''}
                onChange={(e) => {
                  const template = templates.find(t => t.id === e.target.value) || null;
                  handleTemplateChange(template);
                }}
                startAdornment={<TemplateIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="">
                  <em>No Template</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.templateName} ({template.templateCategory})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Email Subject */}
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              required
              placeholder="Enter email subject..."
            />

            {/* Variable Insertion */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Insert Variables
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {availableVariables.map((variable) => (
                  <Chip
                    key={variable}
                    label={`{${variable}}`}
                    size="small"
                    onClick={() => insertVariable(variable)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            {/* Custom Message */}
            <TextField
              label="Custom Message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional custom message that will replace {customMessage} variable..."
              helperText="This message will be inserted wherever you use {customMessage} in your email"
            />

            {/* Email Body */}
            <TextField
              label="Email Body"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              fullWidth
              multiline
              rows={12}
              required
              placeholder="Enter your email content here. Use {variableName} for dynamic content..."
            />

            {/* Email Options */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as 'High' | 'Normal' | 'Low')}
                >
                  <MenuItem value="High">High</MenuItem>
                  <MenuItem value="Normal">Normal</MenuItem>
                  <MenuItem value="Low">Low</MenuItem>
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={scheduledSend}
                    onChange={(e) => setScheduledSend(e.target.checked)}
                  />
                }
                label="Schedule Send"
              />

              {scheduledSend && (
                <DateTimePicker
                  label="Send Time"
                  value={scheduledTime}
                  onChange={setScheduledTime}
                  slotProps={{
                    textField: { size: 'small' }
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              {onSave && (
                <Button
                  onClick={handleSaveDraft}
                  disabled={loading}
                  startIcon={<SaveIcon />}
                >
                  Save Draft
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={generatePreview}
                disabled={loading || !subject || !emailBody}
                startIcon={<PreviewIcon />}
              >
                Preview
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  if (selectedRecipients.length === 0) {
                    alert('Please select at least one recipient');
                    return;
                  }
                  if (selectedRecipients.length > 1) {
                    alert('Export feature works with single recipient only. Please select one candidate.');
                    return;
                  }
                  if (!selectedTemplate) {
                    alert('Please select an email template first');
                    return;
                  }
                  openExportDialog(selectedTemplate, selectedRecipients[0], {
                    customMessage: customMessage
                  });
                }}
                disabled={loading}
                startIcon={<ExportIcon />}
                sx={{
                  borderColor: 'success.main',
                  color: 'success.main',
                  '&:hover': {
                    borderColor: 'success.dark',
                    backgroundColor: 'success.50'
                  }
                }}
              >
                Export for Outlook
              </Button>
              <Button
                variant="contained"
                onClick={handleSend}
                disabled={loading || !subject || !emailBody}
                startIcon={scheduledSend ? <ScheduleIcon /> : <SendIcon />}
              >
                {scheduledSend ? 'Schedule Email' : 'Send Email'}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Email Export Dialog */}
      <EmailExportDialog
        open={exportState.isExportDialogOpen}
        onClose={closeExportDialog}
        template={exportState.selectedTemplate}
        candidate={exportState.selectedCandidate}
        customVariables={exportState.customVariables}
      />
    </LocalizationProvider>
  );
};

export default EmailComposer;